import { gql } from "lib/apolloClient";
import { studentsAlertFragment } from "modules/CommonFragments";

export const userFragment = {
  userItem: gql`
    fragment userItem on User {
      id
      firstName
      lastName
      middleName
      type

      ... on Student {
        preferredName
        prefix
        pronouns
        suffix
      }
    }
  `,

  userChatViewOnly: gql`
    fragment userChatViewOnly on User {
      id
      isChatViewOnly(chatId: $chatId)
    }
  `,

  userDetailItem: gql`
    fragment userDetailItem on User {
      id
      firstName
      lastName
      middleName
      type
      profileImage

      ... on Student {
        isArchived
        isDeleted
        preferredName
        prefix
        pronouns
        suffix
        yearGroup {
          id
          grade {
            id
            name
          }
        }
      }

      ... on Staff {
        isArchived
        isDeleted
        displayRole
        role
        preferredName
        pronouns
        suffix
        prefix
      }

      ... on FamilyMember {
        preferredName
        pronouns
        suffix
        prefix
        children(organizationId: $organizationId) {
          id
          firstName
          lastName
          middleName
          preferredName
          pronouns
          prefix
          suffix
        }
      }
    }
  `,

  userDetailsItemWithBlockStatus: gql`
    fragment userDetailsItemWithBlockStatus on User {
      id
      firstName
      lastName
      middleName
      type
      profileImage
      isChatViewOnly(chatId: $chatId)

      ... on Student {
        isArchived
        isDeleted
        preferredName
        prefix
        pronouns
        suffix
        yearGroup {
          id
          grade {
            id
            name
          }
        }
      }

      ... on Staff {
        isArchived
        isDeleted
        displayRole
        role
        preferredName
        pronouns
        suffix
        prefix
      }

      ... on FamilyMember {
        preferredName
        pronouns
        suffix
        prefix
        children(organizationId: $organizationId) {
          id
          firstName
          lastName
          middleName
          preferredName
          pronouns
          prefix
          suffix
        }
      }
    }
  `,

  userDetailsWithParent: gql`
    fragment userDetailItem on User {
      id
      firstName
      lastName
      type
      profileImage
      ... on Student {
        middleName
        preferredName
        pronouns
        prefix
        suffix
        yearGroup {
          id
          grade {
            id
            name
          }
        }
      }
      ... on Staff {
        displayRole
        preferredName
        pronouns
        suffix
        prefix
      }
      ... on FamilyMember {
        preferredName
        pronouns
        suffix
        prefix
        children(organizationId: $organizationId) {
          id
          firstName
          lastName
          middleName
          preferredName
          pronouns
          prefix
          suffix
        }
      }
    }
  `,

  chatUserV2DetailsItem: gql`
    fragment chatUserV2DetailsItem on User {
      id
      firstName
      lastName
      middleName
      type
      profileImage

      ... on Student {
        isArchived
        isDeleted
        preferredName
        prefix
        pronouns
        suffix
        yearGroup {
          id
          grade {
            id
            name
          }
        }
        courses {
          id
          title
          isArchived
        }
      }

      ... on Staff {
        isArchived
        isDeleted
        displayRole
        role
        preferredName
        pronouns
        suffix
        prefix
        courses {
          id
          title
          isArchived
        }
      }
      ... on FamilyMember {
        preferredName
        pronouns
        suffix
        prefix
        children(organizationId: $organizationId) {
          id
          profileImage
          firstName
          middleName
          lastName
          preferredName
          pronouns
          prefix
          suffix
          yearGroup {
            id
            grade {
              id
              name
            }
          }
        }
      }
    }
  `,

  targetUserItem: gql`
    fragment targetUserItem on User {
      id
      firstName
      lastName
      type
      profileImage
    }
  `,

  userDetailItemWithCurriculum: gql`
    fragment userDetailItemWithCurriculum on User {
      id
      firstName
      lastName
      middleName
      type
      profileImage

      ... on Student {
        isArchived
        isDeleted
        preferredName
        prefix
        pronouns
        suffix
        yearGroup {
          id
          grade {
            id
            name
          }
          curriculumProgram {
            id
            label
          }
        }
      }

      ... on Staff {
        isArchived
        isDeleted
        displayRole
        role
        preferredName
        pronouns
        suffix
        prefix
        curriculumPrograms {
          id
          label
        }
      }

      ... on FamilyMember {
        preferredName
        pronouns
        suffix
        prefix
        children(organizationId: $organizationId) {
          id
          firstName
          lastName
          middleName
          preferredName
          pronouns
          prefix
          suffix
        }
      }
    }
  `,
};

export const messageV2ReactionFragment = {
  messageV2ReactionItem: gql`
    fragment reactionItem on MessageV2Reaction {
      id
      uid
      unified
      skinTone
      createdBy {
        id
        firstName
      }
    }
  `,
};

export const interactiveFragment = {
  pollMessage: gql`
    fragment pollMessage on MessageV2Poll {
      id
      isResultPublic
      isMultipleVoteAllowed
      isResponseEditAllowed
      isResponseActive
      expiresAt
      question
      options {
        label
        selectedByUsersCount
        metadata
        isSelectedByUser
        id
      }
    }
  `,
  formMessage: gql`
    fragment formMessage on MessageV2Form {
      id
      isResponseActive
      isRespondedByUser
      title
      description
      createdBy {
        id
      }
    }
  `,
};

export const messageV2AttachmentFragment = {
  attachmentItem: gql`
    fragment attachmentItem on Attachment {
      id
      name
      type
      mimeType
      url
      signedUrl
      thumbUrl
      title
      metadata
      streamUrl
      parentType
      createdBy {
        id
        type
      }

      interaction {
        ... on MessageV2Poll {
          ...pollMessage
        }
        ... on MessageV2Form {
          ...formMessage
        }
      }
    }
    ${interactiveFragment.pollMessage}
    ${interactiveFragment.formMessage}
  `,
};

export const messageFragment = {
  messageWithReactionsItem: gql`
    fragment messageWithReactionsItem on MessageV2 {
      id
      reactions {
        ...reactionItem
      }
    }
    ${messageV2ReactionFragment.messageV2ReactionItem}
  `,
  messageV2ItemDetail: gql`
    fragment messageV2ItemDetail on MessageV2 {
      id
      label
      type # NORMAL | CHANNEL_ACTIVITY
      isRead
      cursor
      isDeleted
      createdAt
      updatedAt
      isFlagged
      summaryRange {
        start
        end
      }
      createdBy {
        id
        firstName
        lastName
        middleName
        type
        profileImage
        ... on Staff {
          isArchived
          isDeleted
          preferredName
          pronouns
          suffix
          prefix
        }
        ... on Student {
          isArchived
          isDeleted
          preferredName
          prefix
          pronouns
          suffix
        }
        ... on FamilyMember {
          preferredName
          prefix
          pronouns
          suffix
        }
      }
      reactions {
        ...reactionItem
      }

      parentMessage {
        id
        label
        isDeleted
        cursor
        createdAt
        updatedAt
        type
        createdBy {
          id
          firstName
          lastName
          middleName
          profileImage
          ... on Staff {
            isArchived
            isDeleted
            preferredName
            pronouns
            suffix
            prefix
          }
          ... on Student {
            isArchived
            isDeleted
            preferredName
            prefix
            pronouns
            suffix
          }
        }
        attachments {
          ...attachmentItem
        }
      }
      attachments {
        ...attachmentItem
      }
      channelActivity {
        type
        ... on MessageV2ChannelDescriptionChange {
          after
        }
        ... on MessageV2ChannelLabelChange {
          after
          before
        }
        ... on MessageV2ChannelUserAdd {
          targetUsers {
            ...userDetailItem
            ... on Student {
              alertTypes {
                ...alertTypeFields
              }
            }
          }
        }
        ... on MessageV2ChannelUserRemove {
          targetUsers {
            ...userDetailItem
            ... on Student {
              alertTypes {
                ...alertTypeFields
              }
            }
          }
        }
        ... on MessageV2ChannelMessageMoveFrom {
          chat {
            id
            label
          }

          isAllMessagesMoved
        }

        ... on MessageV2ChannelTypeChange {
          before
          after
        }

        ... on MessageV2ChannelBannerMessageChange {
          before
          after
        }

        ... on MessageV2ChannelMessageMoveTo {
          isAllMessagesMoved
        }
      }
    }
    ${messageV2AttachmentFragment.attachmentItem}
    ${messageV2ReactionFragment.messageV2ReactionItem}
    ${userFragment.userDetailItem}
    ${studentsAlertFragment}
  `,
};

const basicUserFragment = gql`
  fragment userDetailItem on User {
    id
    firstName
    lastName
    middleName
    type
    profileImage

    ... on Student {
      isArchived
      isDeleted
      preferredName
      prefix
      pronouns
      suffix
      yearGroup {
        id
        grade {
          id
          name
        }
      }
      sourceId
      alertTypes {
        ...alertTypeFields
      }
    }

    ... on Staff {
      isArchived
      isDeleted
      displayRole
      role
      preferredName
      prefix
      suffix
      pronouns
    }

    ... on FamilyMember {
      preferredName
      prefix
      pronouns
      suffix
      children(organizationId: $organizationId) {
        id
        firstName
        lastName
        middleName
        preferredName
        prefix
        pronouns
        suffix
        sourceId
      }
    }
    ... on Parent {
      preferredName
      prefix
      pronouns
      suffix
      children {
        id
        firstName
        lastName
        middleName
        preferredName
        prefix
        pronouns
        suffix
        sourceId
      }
    }
  }
  ${studentsAlertFragment}
`;

const basicUserFragmentV2 = gql`
  fragment userDetailItemV2 on User {
    id
    firstName
    lastName
    middleName
    type
    profileImage

    ... on Student {
      isArchived
      isDeleted
      preferredName
      prefix
      pronouns
      suffix
      sourceId
      yearGroup {
        id
        grade {
          id
          name
        }
        curriculumProgram @include(if: $includeCurriculumPrograms) {
          id
          label
        }
      }
      alertTypes {
        ...alertTypeFields
      }
    }

    ... on Staff {
      isArchived
      isDeleted
      displayRole
      role
      preferredName
      prefix
      suffix
      pronouns
      curriculumPrograms @include(if: $includeCurriculumPrograms) {
        id
        label
      }
    }

    ... on FamilyMember {
      preferredName
      prefix
      pronouns
      suffix
      children(organizationId: $organizationId) {
        id
        firstName
        lastName
        middleName
        preferredName
        prefix
        pronouns
        suffix
        sourceId
      }
    }
  }
  ${studentsAlertFragment}
`;

// common fragments for manage members
export const manageMembersFragments = {
  basicUserItem: basicUserFragment,
  basicUserItemV2: basicUserFragmentV2,
  userItem: gql`
    fragment userItem on User {
      ...userDetailItem

      ... on Student {
        sourceId
        isArchived
        isDeleted
        preferredName
        pronouns
        prefix
        suffix

        yearGroup {
          id
          grade {
            id
            name
          }
        }

        familyMembers @include(if: $getStudentFamilyMembers) {
          id
          firstName
          lastName
          middleName
          type
          profileImage
          preferredName
          pronouns
          suffix
          prefix

          children(organizationId: $organizationId) {
            id
            firstName
            lastName
            middleName
            preferredName
            pronouns
            suffix
            prefix
            sourceId
          }
        }
      }
    }
    ${basicUserFragment}
  `,
  userItemV2: gql`
    fragment userItemV2 on User {
      ...userDetailItemV2

      ... on Student {
        sourceId
        isArchived
        isDeleted
        preferredName
        pronouns
        prefix
        suffix

        yearGroup {
          id
          grade {
            id
            name
          }
          curriculumProgram @include(if: $includeCurriculumPrograms) {
            id
            label
          }
        }

        familyMembers @include(if: $getStudentFamilyMembers) {
          id
          firstName
          lastName
          middleName
          type
          profileImage
          preferredName
          pronouns
          suffix
          prefix

          children(organizationId: $organizationId) {
            id
            firstName
            lastName
            middleName
            preferredName
            pronouns
            suffix
            prefix
            sourceId
          }
        }
      }
    }
    ${basicUserFragmentV2}
  `,
};
